import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useTheme } from 'styled-components'
import { DataTable, Select, Typography, useSelect } from '@chilecompra/react-kit/components'
import { capitalizer, formatAmount, formatPercentage, truncate } from '../../modules/utils/formatters'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import generateYearsBetween from '../../modules/utils/years'
import { onComparatorModalitiesThunk } from './ComparatorBuyingModalities.actions'
import ComparatorBarChart from '../../components/Charts/ComparatorBarChart'
import IconButton from '../../components/Buttons/IconButton'
import Infobar from '../../components/Infobar/Infobar'
import InfoLabel from '../../components/Labels/InfoLabel'
import ComparadorBuyingModalities from '../../components/Infographics/ComparadorBuyingModalities'
import { Wrapper, GridWrapper, Link } from './ComparatorBuyingModalities.styles'

/**
 * The ComparatorBuyingModalities's container.
 */
const ComparatorBuyingModalities = ({ firstEntity, secondEntity, latestUpdate }) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const inputBarChartRef = useRef()
  const dataTableRef = useRef()
  const { endYear, startYear } = useSelector(state => state.topNavbar)
  const years = generateYearsBetween(endYear, startYear)
  const [showTable, setShowTable] = useState(false)
  const [expandArrow, setExpandArrow] = useState(false)
  const [fileName, setFileName] = useState(`Mecanismos_Compra_${years[0].value}`)
  const {
    data: { categories, chart, csvStructure },
    fetching: loading
  } = useSelector(state => state.comparatorModalities)
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: years[0].value,
    changeCallback: updatedValue => {
      dataTableRef.current?.setPage(1)
      dispatch(
        onComparatorModalitiesThunk(
          { year: updatedValue, idEntity: [firstEntity.value, secondEntity.value] },
          distValue,
          firstEntity.name,
          secondEntity.name
        )
      )
      setFileName(`Mecanismos_Compra_${updatedValue}`)
    }
  })
  const { value: distValue, onChange: handleChangeDist } = useSelect({
    initialValue: 'amount',
    changeCallback: updatedValue => {
      dispatch(
        onComparatorModalitiesThunk(
          { year: yearValue, idEntity: [firstEntity.value, secondEntity.value] },
          updatedValue,
          firstEntity.name,
          secondEntity.name
        )
      )
    }
  })
  const distribution = [
    { name: 'Monto en pesos', value: 'amount' },
    { name: 'Órdenes de compra', value: 'oc' },
    { name: 'Monto Porcentual', value: 'percentage' }
  ]
  const handleShowTable = updatedValue => setShowTable(updatedValue)

  const handleCaptureImage = () => exportToImage('ComparatorBuyingModalities', fileName)
  const handleDownloadFile = () => exportToCsv(csvStructure, fileName)

  useEffect(() => {
    dispatch(
      onComparatorModalitiesThunk(
        { year: years[0].value, idEntity: [firstEntity.value, secondEntity.value] },
        'amount',
        firstEntity.name,
        secondEntity.name
      )
    )
  }, [firstEntity, secondEntity])

  return (
    <Wrapper margin="64px auto 0">
      <GridWrapper container justifyContent="center">
        <GridWrapper item xs={12}>
          <Typography variant="h2" fontSize="22px" fontWeight="bold">
            Mecanismos de compra
          </Typography>
          <Typography variant="body2" fontSize="16px" lineHeight="20px" margin="16px 0 48px">
            Los ministerios, servicios y distintos organismos del Estado compran sus productos y servicios utilizando
            distintos mecanismos o procedimientos de compra.
            <br />
            <br />A continuación podrás revisar los mecanismos utilizados por ambos organismos públicos a través de la
            plataforma Mercado Público. A partir del año 2020 se destaca la Compra Ágil como modalidad de compra de
            trato directo que se caracteriza por ser abierta, competitiva y transparente.
            <br />
            <br />
            Según el año que consultes podrás conocer los montos transados y el número de órdenes de compra asociados a
            cada mecanismo. Recuerda que estos montos que se extraen de la orden de compra que emite una entidad en{' '}
            <Link href="https://www.mercadopublico.cl/Home">www.mercadopublico.cl</Link>, corresponden a compromisos de
            los organismos, no a pagos.
            <br />
            <br />
            La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto +
            impuestos).
          </Typography>
        </GridWrapper>
      </GridWrapper>
      <GridWrapper container alignItems="center" justifyContent="center">
        <GridWrapper container item align="left" md={10} spacing={1} xs={9}>
          <GridWrapper item align="left" md={3} xs={12}>
            <Select label="AÑO" options={years} value={yearValue} onChange={handleChangeYear} />
          </GridWrapper>
          <GridWrapper item align="left" md={5} xs={12}>
            <Select label="DISTRIBUCIÓN" options={distribution} value={distValue} onChange={handleChangeDist} />
          </GridWrapper>
        </GridWrapper>
        <GridWrapper item align="right" md={2} xs={12} margin="6px 0 0">
          <IconButton
            type="toggleChart"
            variant="outlined"
            singleIcon
            margin="24px 10px 0 0"
            disabled={!showTable}
            onClick={() => handleShowTable(false)}
          />
          <IconButton
            type="toggleTable"
            variant="outlined"
            singleIcon
            disabled={showTable}
            margin="24px 0 0"
            onClick={() => handleShowTable(true)}
          />
        </GridWrapper>
      </GridWrapper>
      {showTable ? (
        <GridWrapper container justifyContent="center" margin="24px 0 0">
          <GridWrapper item xs={12}>
            <DataTable
              headers={[
                { label: 'Organismo comprador', value: 0 },
                { label: 'Año', value: 1 },
                { label: 'Mecanismo de compra', value: 2 },
                { label: 'N° de órdenes de compra', value: 3 },
                { label: 'Monto total transado', value: 4 },
                { label: 'Monto porcentual', value: 5 }
              ]}
              headersCellsProps={[
                { enableSort: true },
                null,
                { enableSort: true },
                { enableSort: true },
                { enableSort: true },
                { enableSort: true }
              ]}
              initialRows={6}
              loading={loading}
              records={csvStructure.slice(1)}
              recordsDecorators={[
                ([entity]) => truncate(capitalizer(entity), 40),
                ([, year]) => year,
                ([, , category]) => category,
                ([, , , totalOCs]) => totalOCs,
                ([, , , , amount]) => `$${formatAmount(amount)}`,
                ([, , , , , percentage]) => formatPercentage(percentage)
              ]}
              ref={dataTableRef}
            />
          </GridWrapper>
        </GridWrapper>
      ) : (
        <>
          <GridWrapper container justifyContent="center">
            <GridWrapper item xs={12}>
              <ComparatorBarChart
                comparator
                categories={categories}
                data={chart}
                distValue={distValue}
                fileName={fileName}
                height={600}
                id="ComparatorBuyingModalities"
                inputBarChartRef={inputBarChartRef}
              />
              <GridWrapper container justifyContent="flex-end">
                {distValue === 'amount' ? (
                  <InfoLabel label="Millones de pesos (CLP)" margin="0 0 16px" />
                ) : (
                  <InfoLabel label="N° de órdenes de compra" margin="0 0 16px" />
                )}
              </GridWrapper>
            </GridWrapper>
          </GridWrapper>
          <GridWrapper container justifyContent="center">
            <GridWrapper item xs={12}>
              <InfoLabel label={firstEntity.name} color={theme.palette.charts.navyBlue} margin="0 24px 0 0" />
              <InfoLabel label={secondEntity.name} color={theme.palette.charts.darkRed} />
            </GridWrapper>
          </GridWrapper>
        </>
      )}

      <GridWrapper container justifyContent="center" margin="48px 0 0">
        <GridWrapper item xs={12}>
          <Infobar
            disableDownloadChart={showTable}
            disableDownloadCsv={!showTable}
            disableInfographic={showTable}
            expandArrow={expandArrow}
            infographic={<ComparadorBuyingModalities />}
            latestUpdate={latestUpdate}
            onCaptureImage={handleCaptureImage}
            onDownloadFile={handleDownloadFile}
            setExpandArrow={setExpandArrow}
          />
        </GridWrapper>
      </GridWrapper>
    </Wrapper>
  )
}

ComparatorBuyingModalities.propTypes = {
  firstEntity: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.number
  }),
  secondEntity: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.number
  }),
  latestUpdate: PropTypes.string
}

export default ComparatorBuyingModalities
